export type ActivityDetailColumnAlign = 'left' | 'center' | 'right';

export function getActivityDetailFlexAlign(
  align: ActivityDetailColumnAlign
): 'flex-start' | 'center' | 'flex-end' {
  if (align === 'right') {
    return 'flex-end';
  }
  if (align === 'center') {
    return 'center';
  }
  return 'flex-start';
}

export type ActivityDetailTableLayoutVariant = 'expanded' | 'collapsed';

export type ActivityDetailColumnLayout = {
  width: string;
  align: ActivityDetailColumnAlign;
  px?: number;
  allowWrap?: boolean;
};

export type ActivityDetailStageGroup = {
  key: 'Checkout' | 'SO' | 'PGI' | 'POD';
  label: string;
  subLabel?: string;
};

export const ACTIVITY_DETAIL_STAGE_GROUPS: ActivityDetailStageGroup[] = [
  { key: 'Checkout', label: 'Checkout' },
  { key: 'SO', label: 'SO', subLabel: 'Diproses' },
  { key: 'PGI', label: 'PGI', subLabel: 'Dikirim' },
  { key: 'POD', label: 'POD', subLabel: 'Diterima' },
];

/** Hidden when table is collapsed. */
export const EXPANDED_ONLY_COLUMN_IDS = [
  'so_qty',
  'so_harga',
  'so_subtotal',
  'pgi_qty',
  'pgi_harga',
  'pgi_subtotal',
  'cancel_reason_id',
] as const;

const expandedQtyLayout: ActivityDetailColumnLayout = {
  width: '72px',
  align: 'center',
  px: 2,
};

const expandedHargaLayout: ActivityDetailColumnLayout = {
  width: '152px',
  align: 'center',
  allowWrap: true,
  px: 3,
};

const expandedSubtotalLayout: ActivityDetailColumnLayout = {
  width: '136px',
  align: 'center',
  px: 3,
};

const collapsedQtyLayout: ActivityDetailColumnLayout = {
  width: '72px',
  align: 'center',
  px: 2,
};

const collapsedHargaLayout: ActivityDetailColumnLayout = {
  width: '140px',
  align: 'center',
  allowWrap: true,
  px: 3,
};

const collapsedSubtotalLayout: ActivityDetailColumnLayout = {
  width: '128px',
  align: 'center',
  px: 3,
};

const EXPANDED_COLUMN_LAYOUT: Record<string, ActivityDetailColumnLayout> = {
  select: { width: '48px', align: 'center', px: 1 },
  item_code: { width: '120px', align: 'left', px: 3 },
  product: { width: '320px', align: 'left', allowWrap: true, px: 3 },
  note: { width: '172px', align: 'left', allowWrap: true, px: 3 },
  valuation_type: { width: '148px', align: 'left', allowWrap: true, px: 3 },
  checkout_qty: expandedQtyLayout,
  checkout_harga: expandedHargaLayout,
  checkout_subtotal: expandedSubtotalLayout,
  so_qty: expandedQtyLayout,
  so_harga: expandedHargaLayout,
  so_subtotal: expandedSubtotalLayout,
  pgi_qty: expandedQtyLayout,
  pgi_harga: expandedHargaLayout,
  pgi_subtotal: expandedSubtotalLayout,
  pod_qty: expandedQtyLayout,
  pod_harga: expandedHargaLayout,
  pod_subtotal: expandedSubtotalLayout,
  product_storage: { width: '112px', align: 'center', px: 3 },
  is_updated: { width: '152px', align: 'center', allowWrap: true, px: 3 },
  cancel_reason_id: {
    width: '176px',
    align: 'center',
    allowWrap: true,
    px: 3,
  },
};

const COLLAPSED_COLUMN_LAYOUT: Record<string, ActivityDetailColumnLayout> = {
  select: { width: '48px', align: 'center', px: 1 },
  item_code: { width: '120px', align: 'left', px: 3 },
  product: { width: '300px', align: 'left', allowWrap: true, px: 3 },
  note: { width: '180px', align: 'left', allowWrap: true, px: 3 },
  valuation_type: { width: '140px', align: 'left', allowWrap: true, px: 3 },
  checkout_qty: collapsedQtyLayout,
  checkout_harga: collapsedHargaLayout,
  checkout_subtotal: collapsedSubtotalLayout,
  so_qty: collapsedQtyLayout,
  so_harga: collapsedHargaLayout,
  so_subtotal: collapsedSubtotalLayout,
  pgi_qty: collapsedQtyLayout,
  pgi_harga: collapsedHargaLayout,
  pgi_subtotal: collapsedSubtotalLayout,
  pod_qty: collapsedQtyLayout,
  pod_harga: collapsedHargaLayout,
  pod_subtotal: collapsedSubtotalLayout,
  product_storage: { width: '112px', align: 'center', px: 3 },
  is_updated: { width: '152px', align: 'center', allowWrap: true, px: 3 },
  cancel_reason_id: {
    width: '176px',
    align: 'center',
    allowWrap: true,
    px: 3,
  },
};

export const ACTIVITY_DETAIL_COLUMN_ORDER = [
  'select',
  'item_code',
  'product',
  'note',
  'valuation_type',
  'checkout_qty',
  'checkout_harga',
  'checkout_subtotal',
  'so_qty',
  'so_harga',
  'so_subtotal',
  'pgi_qty',
  'pgi_harga',
  'pgi_subtotal',
  'pod_qty',
  'pod_harga',
  'pod_subtotal',
  'product_storage',
  'is_updated',
  'cancel_reason_id',
] as const;

export function getVisibleColumnIds(isExpanded: boolean): string[] {
  if (isExpanded) {
    return [...ACTIVITY_DETAIL_COLUMN_ORDER];
  }

  return ACTIVITY_DETAIL_COLUMN_ORDER.filter(
    (columnId) =>
      !EXPANDED_ONLY_COLUMN_IDS.includes(
        columnId as (typeof EXPANDED_ONLY_COLUMN_IDS)[number]
      )
  );
}

export function getVisibleStageGroups(
  isExpanded: boolean
): ActivityDetailStageGroup[] {
  if (isExpanded) {
    return ACTIVITY_DETAIL_STAGE_GROUPS;
  }

  return ACTIVITY_DETAIL_STAGE_GROUPS.filter(
    (group) => group.key === 'Checkout' || group.key === 'POD'
  );
}

export function getActivityDetailTableMinWidth(isExpanded: boolean): string {
  const layout = isExpanded ? EXPANDED_COLUMN_LAYOUT : COLLAPSED_COLUMN_LAYOUT;
  const visibleIds = getVisibleColumnIds(isExpanded);

  const total = visibleIds.reduce((sum, columnId) => {
    const width = parseInt(layout[columnId]?.width ?? '0', 10);
    return sum + width;
  }, 0);

  return `${total}px`;
}

export function getActivityDetailColumnLayout(
  columnId: string,
  isExpanded: boolean
): ActivityDetailColumnLayout {
  const layout = isExpanded ? EXPANDED_COLUMN_LAYOUT : COLLAPSED_COLUMN_LAYOUT;

  return (
    layout[columnId] ?? {
      width: '112px',
      align: 'left',
    }
  );
}

export function isExpandedOnlyColumn(columnId: string): boolean {
  return EXPANDED_ONLY_COLUMN_IDS.includes(
    columnId as (typeof EXPANDED_ONLY_COLUMN_IDS)[number]
  );
}
